import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LoginInput } from "../../../Components/Input/Input";
import styled from "styled-components";
import "@fontsource/montserrat";
import { FRONTEND_LINKS } from "../../../Constants/FrontendLinks";
import { useAuthProvider } from "../../../Providers/AuthProvider.js";
import { MobergLogoSpinner } from "../../../Components/MobergLogoSpinner/MobergLogoSpinner"
import { postWithDependencyInjection } from "../../../Providers/EndpointProvider";
import * as MdIcons from 'react-icons/md';
import { BackButtonDiv, NormalText, InfoMessage } from "../../../Constants/StyledComponents";
import { useWorkspacesProvider } from "../../../Providers/WorkspacesProvider";
import { useOnMount } from "../../../Hooks/useOnMount.js";
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider";
import { MobergFontFamily } from "../../../Components/MobergFont/MobergFont";
import bgImg from "../Login/bgImg.png";
import darkLogoImg from "../Login/moberg-logo-dark.png";
import lightLogoImg from "../Login/moberg-logo-light.png"
import { useMobergTheme } from "../../../Providers/MobergThemeProvider"
import { SOFTWARE_VERSION } from '../../../Version.js';

const CreateAccount = (props) => {
	const authProvider = useAuthProvider()
	const workspaceProvider = useWorkspacesProvider()
	const { LINKS } = useBackendLinksProvider()
	const { colorScheme } = useMobergTheme()
	const logoImg = colorScheme === "dark" ? lightLogoImg : darkLogoImg
	const [email, setEmail] = useState();

	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false)
	const [disableCreateAccountButton, setDisableCreateAccountButton] = useState(true)

	let navigate = useNavigate();

	const query = new URLSearchParams(useLocation().search);

	useOnMount(() => {
		authProvider.logout()
		props.setNavPage(false)

		let token = query.get('token')
		postWithDependencyInjection(token, workspaceProvider.selectedWorkspace, LINKS.ACCOUNT.SETUP_INFO).then((data) => {
			setEmail(data.email)
		}).catch((e) => {
			alert('This invitation link is invalid or has expired. Please contact your administrator for a new link.')
			navigate(FRONTEND_LINKS.MAIN)
		})
	})

	const createAccount = (firstName, lastName, confirmPassword) => {
		let body = {
			first_name: firstName,
			last_name: lastName,
			password: confirmPassword,
		}

		let token = query.get('token')

		return postWithDependencyInjection(token, workspaceProvider.selectedWorkspace, LINKS.ACCOUNT.CREATE_ACCOUNT, body)
	}

	function handleCreateAccount(e) {
		const specialChars = /[`!@$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
		if (e) {
			e.preventDefault()
		}

		if (password !== confirmPassword) {
			alert('Passwords do not match.')
			return
		}

		if (password.includes('#')) {
			alert('Passwords may not contain a hashtag.')
			return
		}

		if (!specialChars.test(password)) {
			alert('Password must contain at least one special character.')
			return
		}
		if (!(/[0-9]/.test(password))) {
			alert('Password must contain at least one number.')
			return
		}
		if (!(String(password).length >= 8)) {
			alert('Password must be at least 8 characters long')
			return
		}

		handleSendEmail()
		createAccount(firstName, lastName, confirmPassword).then((text) => {
			alert('Your account was created. Please check your email for confirmation.')
			authProvider.logout()
			navigate(FRONTEND_LINKS.MAIN)
		}).catch(e => {
			alert(e)
			resetPage()
		})

	}

	function showSendEmailInfoMessage() {
		let sendEmailEmailInfoMessage = document.getElementById("sendingEmailInfoMessage")
		if (!sendEmailEmailInfoMessage) return
		sendEmailEmailInfoMessage.style.display = 'block'
	}

	function hideSendEmailInfoMessage() {
		let sendEmailEmailInfoMessage = document.getElementById("sendingEmailInfoMessage")
		if (!sendEmailEmailInfoMessage) return
		sendEmailEmailInfoMessage.style.display = 'block'
	}

	function handleSendEmail() {
		setDisableCreateAccountButton(true)
		showSendEmailInfoMessage()
	}

	function handleBackToLogin() {
		navigate(FRONTEND_LINKS.MAIN);
	}

	function resetPage() {
		setDisableCreateAccountButton(false)
		hideSendEmailInfoMessage()
	}

	useEffect(() => {
		if (password !== confirmPassword || !password || !confirmPassword || !email || !firstName || !lastName) {
			setDisableCreateAccountButton(true)
		} else {
			setDisableCreateAccountButton(false)
		}
	}, [password, confirmPassword, email, firstName, lastName])

	useEffect(() => {
		props.setNavPage(false)
	}, []);

	return !email ? (
		<LoadingWrapper>
			<LoadingContainer>
				<MobergLogoSpinner />
				<LoadingTitle>Loading...</LoadingTitle>
			</LoadingContainer>
		</LoadingWrapper>
	) : (
		<ClinicalWrapper>
			{/* <LogoContainer>
				<img src={logoImg} alt="Moberg Analytics" style={{ width: '90px' }} />
			</LogoContainer> */}
			<CenteredCreateAccountBox>
				<CreateAccountBox>
					<TopSection>
					<img src={logoImg} alt="Moberg Analytics" style={{ width: '90px' , marginBottom: '16px' }} />
						<CreateAccountHeading>Create account</CreateAccountHeading>
					</TopSection>
					
					<CenterSection>
						<CreateAccountForm>
							<FullWidthField>
								<InputBox>
									<InputLabel>Email</InputLabel>
									<input 
										type="email" 
										placeholder="Email" 
										disabled={true}
										value={email || ""} 
										onChange={(e) => setEmail(e.target.value)}
										style={{ backgroundColor: 'var(--color-input-bg)', color: 'var(--text-secondary-color)' }}
									/>
								</InputBox>
							</FullWidthField>
							
							<FormRow>
								<InputBox>
									<InputLabel>First name <RequiredStar>*</RequiredStar></InputLabel>
									<input 
										type="text" 
										placeholder="First name" 
										value={firstName || ""} 
										onChange={(e) => setFirstName(e.target.value)}
									/>
								</InputBox>
								
								<InputBox>
									<InputLabel>Last name <RequiredStar>*</RequiredStar></InputLabel>
									<input 
										type="text" 
										placeholder="Last name" 
										value={lastName || ""} 
										onChange={(e) => setLastName(e.target.value)}
									/>
								</InputBox>
							</FormRow>
							
						<FormRow>
								<InputBox>
									<InputLabel>Password <RequiredStar>*</RequiredStar></InputLabel>
									<div style={{ flexDirection: "row", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
										<input 
											type={showPassword ? "text" : "password"} 
											placeholder="Password" 
											value={password || ""} 
											onChange={(e) => setPassword(e.target.value)}
											onCopy={(e) => { e.preventDefault() }}
											onPaste={(e) => { e.preventDefault() }}
										/>
										<ShowPasswordIcon onClick={() => setShowPassword(!showPassword)}>
											{showPassword ? <MdIcons.MdVisibilityOff size={22} /> : <MdIcons.MdVisibility size={22} />}
										</ShowPasswordIcon>
									</div>
								</InputBox>
								
								<InputBox>
									<InputLabel>Confirm password <RequiredStar>*</RequiredStar></InputLabel>
									<div style={{ flexDirection: "row", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
										<input 
											type={showPassword ? "text" : "password"} 
											placeholder="Confirm password" 
											value={confirmPassword || ""} 
											onChange={(e) => setConfirmPassword(e.target.value)}
											onCopy={(e) => { e.preventDefault() }}
											onPaste={(e) => { e.preventDefault() }}
										/>
										<ShowPasswordIcon onClick={() => setShowPassword(!showPassword)}>
											{showPassword ? <MdIcons.MdVisibilityOff size={22} /> : <MdIcons.MdVisibility size={22} />}
										</ShowPasswordIcon>
									</div>
								</InputBox>
							</FormRow>
							
							<PasswordInfo>
								<strong>Password requirements:</strong> At least 8 characters, one number, and one special character.
							</PasswordInfo>
						</CreateAccountForm>
					</CenterSection>
					
					<BottomSection>
						<NextButton 
							type="button" 
							onClick={handleCreateAccount}
							disabled={disableCreateAccountButton}
							style={{ 
								opacity: disableCreateAccountButton ? "0.5" : "1",
								cursor: disableCreateAccountButton ? "not-allowed" : "pointer"
							}}
						>
						Create account
						<MdIcons.MdArrowForward size={20} style={{ marginLeft: '8px' }} />
						</NextButton>
						
						<BackToLoginButton onClick={handleBackToLogin}>
							Back to sign in
						</BackToLoginButton>
						
						<CreateAccountFooter>
							<div><a href="https://moberganalytics.com" style={{ textDecoration: "underline", color: "inherit" }}>MCP v{SOFTWARE_VERSION}</a></div>
							<div>Copyright © {new Date().getFullYear()} Moberg Analytics, Inc. All rights reserved.</div>
						</CreateAccountFooter>
					</BottomSection>
				</CreateAccountBox>
			</CenteredCreateAccountBox>
		</ClinicalWrapper>
	);
};

const ClinicalWrapper = styled.div`
	background-image: url(${bgImg});
	background-position: center;
	background-size: cover;
	background-repeat: no-repeat;
	background-color: var(--color-surface-primary);
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	width: 100vw;
	height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0);
		[data-color-scheme="dark"] & {
			background: rgba(0, 0, 0, 0.75);
		}
	}

	& > * {
		position: relative;
		z-index: 1;
	}
`;

const CenteredCreateAccountBox = styled.div`
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: center;
	align-self: center;
	justify-self: center;
	height: auto;
	width: 100%;
	max-width: 800px;
	min-width: 600px;
	padding: 0 20px;
	margin: 0 auto;
	left: 0;
	top: 0;

	@media (max-width: 768px) {
		width: 90%;
		min-width: 280px;
		max-width: 600px;
	}

	@media (max-width: 480px) {
		width: 95%;
		min-width: 260px;
		max-width: 400px;
		padding: 0 10px;
	}
`;

const CreateAccountBox = styled.div`
	width: 100%;
	height: auto;
	min-height: 400px;
	display: flex;
	flex-direction: column;
	box-shadow: var(--modal-box-shadow);
	background: var(--color-surface-panel);
	border-radius: 4px;
	padding: 24px;
`;

const TopSection = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding-top: 0;
	margin-bottom: 8px;
	@media (max-width: 600px) {
		align-items: center;
		padding-top: 0;
		padding-bottom: 8px;
	}
`;

const CenterSection = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-start;
	min-height: 0;
	margin: 8px 0;
`;

const BottomSection = styled.div`
	width: 100%;
	padding-bottom: 16px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	@media (max-width: 600px) {
		padding-bottom: 24px;
	}
`;

const CreateAccountHeading = styled.h1`
	font-family: ${MobergFontFamily.HEADER};
	font-weight: 700;
	margin: 0;
	letter-spacing: 1px;
	font-size: 24px;
	color: var(--color-text-primary);
`;

const CreateAccountForm = styled.form`
	width: 100%;
	max-width: 100%;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 12px;
	@media (max-width: 600px) {
		width: 100%;
		min-width: 0;
		gap: 10px;
	}
`;

const FormRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px;
	
	@media (max-width: 768px) {
		grid-template-columns: 1fr;
		gap: 10px;
	}
`;

const FullWidthField = styled.div`
	grid-column: 1 / -1;
`;

const InputBox = styled.div`
	position: relative;
	margin-bottom: 0;
	input {
		width: 100%;
		padding: 10px 40px 10px 12px;
		border: 1.5px solid var(--color-border-default);
		border-radius: 4px;
		font-size: 14px;
		font-family: inherit;
		outline: none;
		transition: border 0.2s;
		background: var(--color-input-bg);
		color: var(--color-text-primary);
		&:focus {
			border-color: var(--color-accent-interactive);
		}
		&::-ms-reveal {
			display: none;
		}
		&:-webkit-autofill,
		&:-webkit-autofill:hover,
		&:-webkit-autofill:focus {
			-webkit-box-shadow: 0 0 0 1000px var(--color-input-bg) inset;
			-webkit-text-fill-color: var(--color-text-primary);
			caret-color: var(--color-text-primary);
			transition: background-color 5000s ease-in-out 0s;
		}
	}
`;

const InputLabel = styled.label`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	font-size: 14px;
	color: var(--color-text-primary);
	font-weight: 500;
	margin-bottom: 4px;
	margin-left: 2px;
	font-family: inherit;
`;

const OptionalText = styled.span`
	font-size: 12px;
	color: var(--text-secondary-color);
	font-weight: 400;
`;

const ShowPasswordIcon = styled.span`
	position: absolute;
	right: 14px;
	cursor: pointer;
	font-size: 1.2rem;
	user-select: none;
	display: flex;
	align-items: center;
	color: var(--color-text-primary);
	opacity: 0.5;
`;

const PasswordInfo = styled.div`
	font-size: 11px;
	color: var(--text-secondary-color);
	background: var(--color-input-bg);
	padding: 8px 12px;
	border-radius: 4px;
	border-left: 3px solid var(--color-accent-interactive);
	margin-top: 4px;
`;

const NextButton = styled.button`
	width: 100%;
	padding: 10px 0;
	background: var(--color-accent-interactive);
	color: #fff;
	font-size: 16px;
	font-weight: 600;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	transition: filter 0.2s;
	display: flex;
	align-items: center;
	justify-content: center;

	[data-color-scheme="dark"] & {
		background: #207DEA;
		color: #fff;
	}

	&:hover:not(:disabled) {
		filter: brightness(0.85);
	}
`;

const BackToLoginButton = styled.button`
	width: 100%;
	padding: 10px 0;
	background: transparent;
	color: var(--color-accent-interactive);
	font-size: 14px;
	font-weight: 500;
	border: 1.5px solid var(--color-accent-interactive);
	border-radius: 4px;
	cursor: pointer;
	transition: all 0.2s;
	&:hover {
		background: var(--color-accent-interactive);
		color: var(--color-hover-active-blue-text, #fff);
	}
`;

const CreateAccountFooter = styled.div`
	font-size: 12px;
	color: var(--color-text-primary);
	opacity: 0.5;
	text-align: center;
	display: flex;
	flex-direction: column;
	gap: 2px;
	div {
		margin: 0;
	}
	a {
		color: var(--color-accent-interactive);
		opacity: 1;
		text-decoration: none;
		&:hover {
			text-decoration: underline;
		}
	}
`;

const LoadingWrapper = styled.div`
	background-color: var(--color-surface-primary);
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	width: 100vw;
	height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const LoadingContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	gap: 24px;
`;

const LoadingTitle = styled.h2`
	font-family: ${MobergFontFamily.HEADER};
	font-weight: 700;
	font-size: 20px;
	color: var(--color-text-primary);
	margin: 0;
	letter-spacing: 0.3px;
`;

const RequiredStar = styled.span`
	color: #e74c3c;
	font-weight: bold;
	margin-left: 2px;
`;

export default CreateAccount;

